import { useMemo } from 'react';
import { PDG_TEAMS } from '@cortex/domain-product-innovation-lab-combat-power-scope';
import type { CombatPowerResponse } from '../../../types/combat-power.js';
import type { MemberOrganization } from '../../../types/api.js';
import { OverallChart } from './OverallChart.js';
import { AceTeamsChart } from './AceTeamsChart.js';
import { TeamQuarterChart } from './TeamQuarterChart.js';

interface TeamBudgetChartProps {
  data: CombatPowerResponse;
  selectedOrg: MemberOrganization | undefined;
  memberTeamMap: Map<string, { group: string; team: string }>;
  currentMonthIdx: number;
}

/** @graph-connects none */
const TEAM_FILTER_MAP: Record<string, { group: string; teams: string[] }> = {
  // pdg ビューは PDG 全体 (pi-tet + pi_sret)。従来はチャート非表示だったが、
  // Qサマリーを全フィルタで表示するようにした際、左側だけ空くレイアウトになるため
  // PDG 全体のチャートを表示する
  pdg: { group: 'pi_pdg', teams: [...PDG_TEAMS] },
  tet: { group: 'pi_pdg', teams: ['pi-tet'] },
  aircloset: { group: 'ACE', teams: ['airCloset team'] },
  platform: { group: 'ACE', teams: ['Platform team'] },
  mall: { group: 'ACE', teams: ['Mall team'] },
  qa: { group: 'ACE', teams: ['QA team'] },
};

/** @graph-connects none */
const TEAM_LABELS: Record<string, string> = {
  pdg: 'PDG',
  tet: 'PDG TE',
  aircloset: 'ACE airCloset',
  platform: 'ACE Platform',
  mall: 'ACE Mall',
  qa: 'ACE QA',
};

/** @graph-connects none */
export function TeamBudgetChart({
  data,
  selectedOrg,
  memberTeamMap,
  currentMonthIdx,
}: TeamBudgetChartProps) {
  const filteredMembers = useMemo(() => {
    if (!selectedOrg || selectedOrg === 'all' || selectedOrg === 'ace') {
      return [];
    }
    const filter = TEAM_FILTER_MAP[selectedOrg];
    if (!filter) return [];
    return data.members.filter((m) => {
      const info = memberTeamMap.get(m.name.toLowerCase());
      return info && info.group === filter.group && filter.teams.includes(info.team);
    });
  }, [data.members, selectedOrg, memberTeamMap]);

  if (!selectedOrg) {
    return (
      <OverallChart data={data} memberTeamMap={memberTeamMap} currentMonthIdx={currentMonthIdx} />
    );
  }

  if (selectedOrg === 'ace') {
    return (
      <AceTeamsChart data={data} memberTeamMap={memberTeamMap} currentMonthIdx={currentMonthIdx} />
    );
  }

  const teamLabel = TEAM_LABELS[selectedOrg];
  const groupCtx = TEAM_FILTER_MAP[selectedOrg]?.group === 'pi_pdg' ? 'PDG' : 'ACE';
  if (teamLabel && filteredMembers.length > 0) {
    return (
      <TeamQuarterChart
        data={data}
        teamLabel={teamLabel}
        members={filteredMembers}
        groupContext={groupCtx}
        currentMonthIdx={currentMonthIdx}
      />
    );
  }

  return null;
}
